import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import api from '../../api'
import OptionsIcon from '../../shared/components/svgIcons/OptionsIcon'
import RoomResourcePills, { type RoomPreviewItem } from './RoomResourcePills'
import useCloseOnClickAway from '../../shared/hooks/UseCloseOnClickAway'
import { useAuth } from 'react-oidc-context'
import { useAppUser } from '../../context/UserContext/UserContext'


import './roomlist.scss'

type Lroom = {
    id: string,
    name: string,
    lastJoinedAt?: string,
    memberCount?: number,
    trackMembersEnabled?: boolean | null,
    creatorName?: string,
    storageUsed?: number,
    canDelete?: boolean,
    previewItems?: RoomPreviewItem[]
}

const API = process.env.REACT_APP_API_URL;
const ANONYMOUS_ROOM_LIMIT = 9;
const ACCOUNT_ROOM_LIMIT = 12;
const ROOM_LIST_WAKE_REFRESH_AFTER_MS = 10 * 60 * 1000;


const RoomList = () => {


    const [rooms, setRooms] = useState<Lroom[]>([])
    const [recentRooms, setRecentRooms] = useState<Lroom[]>([])
    const [roomsLoading, setRoomsLoading] = useState(true);
    const [roomListError, setRoomListError] = useState('');
    const auth = useAuth();
    const { user: appUser, loading: appUserLoading } = useAppUser();
    const accountAccessToken = auth.user?.access_token;
    const hasAccountSession = auth.isAuthenticated && !!accountAccessToken;
    const roomLimit = hasAccountSession ? ACCOUNT_ROOM_LIMIT : ANONYMOUS_ROOM_LIMIT;
    const hasOwnerBypass = appUser?.plan === 'flashroom-owner';
    const shouldApplyRoomLimit = !hasOwnerBypass && !(hasAccountSession && appUserLoading);

    const [roomMode, setRoomMode] = useState<"YourRooms" | "RecentRooms">("YourRooms");


    const setRoomModeClick = (
        mode: "YourRooms" | "RecentRooms"
    ) => {
        setRoomMode(mode);
    };


    const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
    const [selectedRoomOptions, setSelectedRoomOptions] = useState<string | null>(null);
    const lastWakeRefreshRef = useRef(Date.now());


    const roomOptionsClick = (id: string) => {
        setSelectedRoomOptions(prev => (prev === id ? null : id));
    };

    const cancelRoomOption = () => {
        setSelectedRoomOptions(null)
    }

    const roomOptionRef = useRef<HTMLDivElement | null>(null)


    useCloseOnClickAway({
        targetRef: roomOptionRef,
        onClose: cancelRoomOption
    })






    const setSelectedRoomClick = (roomId: string) => {
        setSelectedRoomId(prev => (prev === roomId ? null : roomId));
    };

    const cencelRoomSelectClick = () => setSelectedRoomId(null);




    const navigate = useNavigate();

    const fetchRooms = useCallback(async () => {
        if (auth.isLoading || appUserLoading) {
            setRoomsLoading(true);
            return;
        }

        const authHeaders = hasAccountSession && accountAccessToken
            ? { Authorization: `Bearer ${accountAccessToken}` }
            : undefined;

        const clientId = localStorage.getItem('flashroom_id');
        if (!authHeaders && !clientId) {
            setRoomsLoading(false);
            return;
        }

        try {
            setRoomsLoading(true);
            setRoomListError('');

            const [ownedRes, recentRes] = await Promise.all([
                authHeaders
                    ? api.get('/rooms/account/owned', { headers: authHeaders })
                    : api.get(`rooms/byclient/${clientId}`),
                authHeaders
                    ? api.get('/rooms/account/recents', { headers: authHeaders })
                    : api.get(`/rooms/recents/${clientId}/loadRecents`),
            ]);

            if (!ownedRes.data.ok) {
                return;
            }

            if (!recentRes.data.ok) {
                return;
            }

            setRooms(
                ownedRes.data.rooms.map((room: Lroom) => ({
                    ...room,
                    canDelete: true,
                }))
            );

            setRecentRooms(
                recentRes.data.roomsData.map((room: { roomId: string; roomName: string; lastJoinedAt?: string; memberCount?: number; trackMembersEnabled?: boolean | null; creatorName?: string; storageUsed?: number; previewItems?: RoomPreviewItem[] }) => ({
                    id: room.roomId,
                    name: room.roomName,
                    lastJoinedAt: room.lastJoinedAt,
                    memberCount: room.memberCount,
                    trackMembersEnabled: room.trackMembersEnabled,
                    creatorName: room.creatorName,
                    storageUsed: room.storageUsed,
                    previewItems: room.previewItems ?? [],
                    canDelete: false,
                }))
            );
        } catch (error) {
            setRoomListError('Could not load rooms. Please try again.');
        } finally {
            setRoomsLoading(false);
        }

    }, [accountAccessToken, appUserLoading, auth.isLoading, hasAccountSession])


    const deleteClick = async (roomId: string) => {
        try {
            setRoomListError('');
            const clientId = localStorage.getItem("flashroom_id");
            if (hasAccountSession && accountAccessToken) {
                await api.delete(`/rooms/account/${roomId}`, {
                    headers: { Authorization: `Bearer ${accountAccessToken}` },
                });
            } else {
                await axios.delete(`${API}/api/rooms/${roomId}/creator/${clientId}/deleteroom`);
            }
            setSelectedRoomId('');

            fetchRooms();
        } catch (error) {
            setRoomListError('Could not delete room. Please try again.');
        }
    }

    const rejoinClick = async (roomId: string) => {
        try {
            setRoomListError('');
            const { data } = hasAccountSession && accountAccessToken
                ? await api.patch(
                    `/rooms/account/${roomId}/rejoin`,
                    undefined,
                    { headers: { Authorization: `Bearer ${accountAccessToken}` } }
                )
                : await (async () => {
                    const clientId = localStorage.getItem('flashroom_id');
                    if (!clientId) throw new Error('missing client id');
                    return axios.patch(`${API}/api/rooms/${roomId}/members/${clientId}/rejoin`);
                })();

            localStorage.setItem("flashroom_roomToken", data.token);
            localStorage.setItem("flashroom_most_recent", roomId);

            navigate(`/room/${roomId}`)
        } catch (error) {
            setRoomListError('Could not join room. Please try again.');
        }
    }

    const formatLastJoined = (value?: string) => {
        if (!value) return '';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';

        const diffMs = Date.now() - date.getTime();
        const minute = 60 * 1000;
        const hour = 60 * minute;
        const day = 24 * hour;
        const month = 30 * day;
        const year = 365 * day;

        if (diffMs < minute) return 'Just now';
        if (diffMs < hour) {
            const minutes = Math.floor(diffMs / minute);
            return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
        }
        if (diffMs < day) {
            const hours = Math.floor(diffMs / hour);
            return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
        }
        if (diffMs < month) {
            const days = Math.floor(diffMs / day);
            return `${days} ${days === 1 ? 'day' : 'days'} ago`;
        }
        if (diffMs < year) {
            const months = Math.floor(diffMs / month);
            return `${months} ${months === 1 ? 'month' : 'months'} ago`;
        }

        const years = Math.floor(diffMs / year);
        return `${years} ${years === 1 ? 'year' : 'years'} ago`;
    }

    const formatStorage = (bytes?: number) => {
        if (!bytes || bytes <= 0) return '0 KB';
        if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
        if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
        return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
    }


    useEffect(() => {
        fetchRooms();
    }, [fetchRooms]);

    useEffect(() => {
        const maybeFetchRooms = (force = false) => {
            const now = Date.now();
            const elapsed = now - lastWakeRefreshRef.current;
            lastWakeRefreshRef.current = now;

            if (force || elapsed >= ROOM_LIST_WAKE_REFRESH_AFTER_MS) {
                void fetchRooms();
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') maybeFetchRooms();
        };

        const onFocus = () => maybeFetchRooms();
        const onOnline = () => maybeFetchRooms(true);

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onFocus);
        window.addEventListener('online', onOnline);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('online', onOnline);
        };
    }, [fetchRooms]);

    useEffect(() => {
        if (rooms.length === 0 && recentRooms.length > 0) {
            setRoomMode("RecentRooms");
            return;
        }

        if (rooms.length > 0 && recentRooms.length === 0) {
            setRoomMode("YourRooms");
        }
    }, [rooms.length, recentRooms.length]);

    const showTabs = rooms.length > 0 && recentRooms.length > 0;
    const visibleRooms = showTabs
        ? (roomMode === "YourRooms" ? rooms : recentRooms)
        : (rooms.length > 0 ? rooms : recentRooms);
    const totalRooms = roomMode === "YourRooms" ? rooms.length : recentRooms.length;

    return (
        <div id="your-rooms">

            <div id='yr-top'>
                <h2>Rooms</h2>
                <span>{totalRooms} total rooms</span>
                <p>Manage rooms you created and quickly jump back into rooms you joined</p>
                {roomListError && <p className="yr-error" role="alert">{roomListError}</p>}


                {!shouldApplyRoomLimit || rooms.length < roomLimit ? (
                    <button className='new-room-rl' onClick={() => navigate('/createroom')}>Create New</button>
                ) : (
                    <div className='limit-reached'>
                        <h3>Room Limit Reached</h3>
                        <p>{hasAccountSession ? 'Accounts can create up to 12 rooms.' : 'Anonymous users can create up to 9 rooms.'}</p>
                    </div>
                )}

            </div>

            {showTabs && (
                <div className='yr-tabs' role="tablist" aria-label="Room lists">
                    <button
                        type="button"
                        className={`yr-tab ${roomMode === 'YourRooms' ? 'active-yr-tab' : ''}`}
                        onClick={() => setRoomModeClick("YourRooms")}
                    >
                        Your Rooms
                        <span>{rooms.length}</span>
                    </button>

                    <button
                        type="button"
                        className={`yr-tab ${roomMode === 'RecentRooms' ? 'active-yr-tab' : ''}`}
                        onClick={() => setRoomModeClick("RecentRooms")}
                    >
                        Join Again
                        <span>{recentRooms.length}</span>
                    </button>
                </div>
            )}
            {roomsLoading ? (
                <div className="yr-empty-state">
                    <h3>Loading rooms...</h3>
                    <p>Getting your rooms ready.</p>
                </div>
            ) : visibleRooms.length === 0 ? (
                <div className="yr-empty-state">
                    <h3>No rooms yet</h3>
                    <p>Create a room or join one to see it here.</p>
                </div>
            ) : (
                <div className="yr-grid">
                    {visibleRooms.map((room) => (
                        <div className="room-card relative" key={room.id}>

                            {room.canDelete && (
                                <div className="rc-options" ref={selectedRoomOptions === room.id ? roomOptionRef : null}>
                                    <button
                                        type="button"
                                        className={`options-btn ${selectedRoomOptions === room.id ? 'active' : ''}`}
                                        aria-label={`Options for ${room.name}`}
                                        aria-expanded={selectedRoomOptions === room.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            roomOptionsClick(room.id);
                                        }}
                                    >
                                        <OptionsIcon />
                                    </button>

                                    {selectedRoomOptions === room.id && (
                                        <div className='room-options-popup'>
                                            <button
                                                type="button"
                                                className='room-option-item'
                                                onClick={() => rejoinClick(room.id)}
                                            >
                                                Join Room
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedRoomClick(room.id);
                                                    cancelRoomOption();
                                                }}
                                                className='room-option-item danger'
                                            >
                                                Delete Room
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {room.canDelete && selectedRoomId === room.id ? (
                                <div className='delete-room-noty'>

                                    <h4>Are you sure you would like to delete this room?</h4>
                                    <div className='dnpbtns'>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteClick(room.id);
                                            }}
                                            className='rm-nty-yes center-text'>Yes</button>

                                        <button onClick={cencelRoomSelectClick}
                                            className='rm-nty-cancel center-text'>cancel</button>

                                    </div>

                                </div>
                            ) : (
                                <></>
                            )

                            }

                            <div className="rc-top">



                                <div className="rc-title">
                                    <span className="rc-icon">
                                        <img src={`${process.env.PUBLIC_URL}/images/room-icon-white.png`} alt="" />
                                    </span>
                                    <h3 title={room.name}>{room.name}</h3>
                                </div>

                            </div>

                            <div className="rc-room-stats">
                                <span>
                                    {room.memberCount ?? 0} {room.trackMembersEnabled === false
                                        ? ((room.memberCount ?? 0) === 1 ? 'view' : 'views')
                                        : ((room.memberCount ?? 0) === 1 ? 'member' : 'members')}
                                </span>
                                {/* <span title={room.creatorName ?? 'Owner'}>By {room.creatorName ?? 'Owner'}</span> */}
                                <span>{formatStorage(room.storageUsed)} used</span>
                            </div>

                            <RoomResourcePills items={room.previewItems} />

                            <div className="rc-actions">
                                <span className="rc-last-joined">
                                    <span className="rc-clock-icon" aria-hidden="true"></span>
                                    {formatLastJoined(room.lastJoinedAt)}
                                </span>
                                <span className='jmp' onClick={() => rejoinClick(room.id)}>Jump In <span className='arrow-r'>&#8250;</span></span>

                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>

    )
}

export default RoomList
